import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { animeList, sortOptions } from "@/mocks/anime";
import { useSeo } from "@/hooks/useSeo";
import AnimeCard from "@/components/feature/AnimeCard";
import FilterPanel from "@/pages/category/components/FilterPanel";
import Pagination from "@/pages/category/components/Pagination";

const PAGE_SIZE = 12;

const parseList = (value: string | null): string[] =>
  value ? value.split(",").filter(Boolean) : [];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<"page" | "load">("page");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const selectedGenres = parseList(searchParams.get("genre"));
  const selectedRegions = parseList(searchParams.get("region"));
  const selectedYears = parseList(searchParams.get("year"));
  const sort = searchParams.get("sort") ?? "latest";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const categoryName = selectedGenres.length
    ? `${selectedGenres.join("、")}动漫`
    : "全部动漫";

  useSeo({
    title: `${categoryName} - 樱花动漫网站`,
    description: `樱花动漫网站「${categoryName}」分类页，汇集热门与经典${categoryName}资源，支持按类型、地区、年份多维筛选与热度排序，快速找到你想看的动漫作品。`,
    keywords: `${categoryName},${selectedGenres.join(",") || "动漫分类"},动漫在线观看,樱花动漫网站,动漫筛选`,
  });

  const toggleParam = (key: string, values: string[], value: string) => {
    const next = values.includes(value)
      ? values.filter((item) => item !== value)
      : [...values, value];
    const params = new URLSearchParams(searchParams);
    if (next.length) params.set(key, next.join(","));
    else params.delete(key);
    params.delete("page");
    setSearchParams(params);
  };

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("genre");
    params.delete("region");
    params.delete("year");
    params.delete("page");
    setSearchParams(params);
  };

  const changeSort = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === "latest") params.delete("sort");
    else params.set("sort", value);
    params.delete("page");
    setSearchParams(params);
  };

  const changePage = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete("page");
    else params.set("page", String(next));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filtered = useMemo(() => {
    const result = animeList.filter((item) => {
      const matchGenre =
        selectedGenres.length === 0 || item.genres.some((g) => selectedGenres.includes(g));
      const matchRegion =
        selectedRegions.length === 0 || selectedRegions.includes(item.region);
      const matchYear =
        selectedYears.length === 0 || selectedYears.includes(String(item.year));
      return matchGenre && matchRegion && matchYear;
    });

    result.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      if (sort === "rating") return b.rating - a.rating;
      return b.year - a.year || b.views - a.views;
    });

    return result;
  }, [selectedGenres, selectedRegions, selectedYears, sort]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedGenres, selectedRegions, selectedYears, sort, mode]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems =
    mode === "page"
      ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      : filtered.slice(0, visibleCount);

  const hasActiveFilter =
    selectedGenres.length > 0 || selectedRegions.length > 0 || selectedYears.length > 0;

  return (
    <div className="mx-auto px-4 py-6 md:px-8 md:py-8">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-700">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/category" className="transition-colors hover:text-primary-700">
          分类
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">{categoryName}</span>
      </nav>

      <div className="mt-4 rounded-lg border border-background-200/70 bg-primary-50/50 p-5 md:p-6">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          {categoryName}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          这里汇集了樱花动漫网站中对应分类的动漫资源，涵盖最新连载与经典作品。你可以结合类型、地区、年份进行多选筛选，并按上传时间、热度或评分排序，快速定位想看的动漫。
        </p>
      </div>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-20 rounded-lg border border-background-200/70 bg-background-50 p-5">
            <FilterPanel
              selectedGenres={selectedGenres}
              selectedRegions={selectedRegions}
              selectedYears={selectedYears}
              onToggleGenre={(v) => toggleParam("genre", selectedGenres, v)}
              onToggleRegion={(v) => toggleParam("region", selectedRegions, v)}
              onToggleYear={(v) => toggleParam("year", selectedYears, v)}
              onClear={clearFilters}
            />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm text-foreground-700 lg:hidden"
            >
              <i className="ri-filter-3-line" />
              筛选
              {hasActiveFilter && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-xs text-background-50">
                  {selectedGenres.length + selectedRegions.length + selectedYears.length}
                </span>
              )}
            </button>

            <span className="text-sm text-foreground-600">
              共 <strong className="text-primary-700">{filtered.length}</strong> 部作品
            </span>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <span className="hidden text-sm text-foreground-500 sm:inline">排序：</span>
              <div className="flex items-center gap-1 rounded-full bg-background-100 px-1 py-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => changeSort(option.value)}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      sort === option.value
                        ? "bg-background-50 text-primary-700"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 rounded-full bg-background-100 px-1 py-1">
                <button
                  type="button"
                  onClick={() => setMode("page")}
                  className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    mode === "page" ? "bg-background-50 text-primary-700" : "text-foreground-600"
                  }`}
                >
                  <i className="ri-layout-grid-line" />
                  分页
                </button>
                <button
                  type="button"
                  onClick={() => setMode("load")}
                  className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    mode === "load" ? "bg-background-50 text-primary-700" : "text-foreground-600"
                  }`}
                >
                  <i className="ri-more-line" />
                  加载更多
                </button>
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-500">
                <i className="ri-emotion-sad-line" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                暂无相关动漫，建议调整筛选条件
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-500">
                当前筛选组合下没有匹配的作品，试着减少筛选条件，或浏览全部分类。
              </p>
              <button
                type="button"
                onClick={clearFilters}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" />
                清空筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {pageItems.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} showDescription />
                ))}
              </div>

              {mode === "page" ? (
                <Pagination page={page} totalPages={totalPages} onChange={changePage} />
              ) : (
                <div className="mt-8 flex justify-center">
                  {visibleCount < filtered.length ? (
                    <button
                      type="button"
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                    >
                      <i className="ri-add-line" />
                      加载更多（剩余 {filtered.length - visibleCount} 部）
                    </button>
                  ) : (
                    <span className="text-sm text-foreground-400">已经到底啦～</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[90] lg:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-foreground-950/50 animate-fade-in"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm overflow-y-auto bg-background-50 p-5 animate-fade-in">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-950">筛选</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel
              selectedGenres={selectedGenres}
              selectedRegions={selectedRegions}
              selectedYears={selectedYears}
              onToggleGenre={(v) => toggleParam("genre", selectedGenres, v)}
              onToggleRegion={(v) => toggleParam("region", selectedRegions, v)}
              onToggleYear={(v) => toggleParam("year", selectedYears, v)}
              onClear={clearFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      )}
    </div>
  );
}